import { useState, useEffect } from "react";
import { Text1, Text2, Text3 } from "@components/ui/texts/Texts";
import { UserAvatar } from "@components/ui/UserAvatar";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Button from "@components/ui/buttons/Button";
import SearchField from "@components/ui/textfields/SearchField";
import TextField from "@components/ui/textfields/TextField";
import DropDown from "@components/ui/dropdown/DropDown";
import { useAuthStore } from "@store/auth";
import { useDataStore } from "@store/user_org";
import { usersApi } from "@services/api";
import { organizationsApi } from "@services/api/organizations";
import { showNotification } from "context/notification.service";
import type { User } from "types/user";

export const Users = () => {
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [loginAsLoading, setLoginAsLoading] = useState<string | null>(null);
  const loginAs = useAuthStore((state) => state.loginAs);
  const isImpersonating = useAuthStore((state) => state.isImpersonating);
  const returnToAdmin = useAuthStore((state) => state.returnToAdmin);
  const userData = useDataStore((state) => state.userData);
  const clearAllData = useDataStore((state) => state.clearAllData);

  // Create Org form
  const [showCreateOrg, setShowCreateOrg] = useState(false);
  const [orgName, setOrgName] = useState("");
  const [orgEmail, setOrgEmail] = useState("");
  const [orgPhone, setOrgPhone] = useState("");
  const [orgSiret, setOrgSiret] = useState("");
  const [orgAddress, setOrgAddress] = useState("");
  const [orgCity, setOrgCity] = useState("");
  const [orgPostal, setOrgPostal] = useState("");
  const [orgType, setOrgType] = useState("garage");
  const [creatingOrg, setCreatingOrg] = useState(false);

  // Create User form
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserName, setNewUserName] = useState("");
  const [newUserOrgId, setNewUserOrgId] = useState("");
  const [newUserAccessType, setNewUserAccessType] = useState("OWNER");
  const [creatingUser, setCreatingUser] = useState(false);

  const fetchAllUsers = async () => {
    setLoading(true);
    try {
      const users = await usersApi.list({ limit: 1000 });
      setAllUsers(users);
    } catch (error) {
      console.error("Failed to fetch all users:", error);
      showNotification({ type: "error", message: "Impossible de charger les utilisateurs" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllUsers();
  }, []);

  const handleLoginAs = async (targetUid: string) => {
    setLoginAsLoading(targetUid);
    try {
      clearAllData();
      await loginAs(targetUid);
      showNotification({ type: "success", message: "Connexion effectuée" });
    } catch (error: any) {
      console.error("Login as failed:", error);
      showNotification({ type: "error", message: "Impossible de se connecter en tant que cet utilisateur" });
    } finally {
      setLoginAsLoading(null);
    }
  };

  const handleReturnToAdmin = async () => {
    try {
      clearAllData();
      await returnToAdmin();
      showNotification({ type: "success", message: "Retour au compte admin" });
    } catch (error) {
      console.error("Return to admin failed:", error);
      showNotification({ type: "error", message: "Impossible de revenir au compte admin" });
    }
  };

  const handleCreateOrg = async () => {
    if (!orgName || !orgEmail) {
      showNotification({ type: "error", message: "Nom et email sont requis" });
      return;
    }
    setCreatingOrg(true);
    try {
      const newOrg = await organizationsApi.create({
        type: orgType,
        name: orgName,
        email: orgEmail,
        phone_number: orgPhone,
        siret: orgSiret,
        city: orgCity,
        postal: orgPostal,
        address: orgAddress,
      });
      showNotification({ type: "success", message: `Organisation "${newOrg.infos.name}" creee (ID: ${newOrg.id})` });
      // Reset form
      setOrgName("");
      setOrgEmail("");
      setOrgPhone("");
      setOrgSiret("");
      setOrgAddress("");
      setOrgCity("");
      setOrgPostal("");
      setShowCreateOrg(false);
    } catch (error: any) {
      console.error("Failed to create org:", error);
      showNotification({ type: "error", message: error?.response?.data?.detail || "Erreur lors de la creation" });
    } finally {
      setCreatingOrg(false);
    }
  };

  const handleCreateUser = async () => {
    if (!newUserEmail || !newUserName || !newUserOrgId) {
      showNotification({ type: "error", message: "Email, nom et org_id sont requis" });
      return;
    }
    setCreatingUser(true);
    try {
      const newUser = await usersApi.create({
        email: newUserEmail,
        display_name: newUserName,
        access_type: newUserAccessType,
        org_id: newUserOrgId,
      } as any);
      // Add user to org members
      await organizationsApi.addMember(newUserOrgId, {
        user_id: newUser.id,
        role: "admin",
      });
      showNotification({ type: "success", message: `Utilisateur "${newUserName}" cree (ID: ${newUser.id})` });
      setNewUserEmail("");
      setNewUserName("");
      setNewUserOrgId("");
      setNewUserAccessType("OWNER");
      setShowCreateUser(false);
      fetchAllUsers();
    } catch (error: any) {
      console.error("Failed to create user:", error);
      showNotification({ type: "error", message: error?.response?.data?.detail || "Erreur lors de la creation" });
    } finally {
      setCreatingUser(false);
    }
  };

  const filteredUsers = allUsers
    .filter((u) => {
      if (!search) return true;
      const s = search.toLowerCase();
      return (
        u.infos?.display_name?.toLowerCase().includes(s) ||
        u.infos?.full_name?.toLowerCase().includes(s) ||
        u.infos?.email?.toLowerCase().includes(s) ||
        u.org_id?.toLowerCase().includes(s)
      );
    })
    .sort((a, b) =>
      (a.infos?.full_name || a.infos?.display_name || "").localeCompare(
        b.infos?.full_name || b.infos?.display_name || "",
      ),
    );

  return (
    <div className="flex flex-col h-fit pb-96 mt-10 mx-4 lg:ml-10 w-full max-w-900">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col">
          <Text1>Utilisateurs</Text1>
          <Text2>Tous les utilisateurs de la plateforme.</Text2>
          <Text3>
            Connectez-vous en tant qu'un autre utilisateur pour diagnostiquer des
            problemes ou effectuer des actions en son nom.
          </Text3>
        </div>

        {isImpersonating && (
          <div className="flex flex-row items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
            <div className="text-h3 font-medium text-amber-800">
              Connecte en tant que {userData?.infos.full_name || userData?.infos.display_name || userData?.infos.email}
            </div>
            <SecondaryButton
              variant="small"
              label="Retour au compte admin"
              onClick={handleReturnToAdmin}
            />
          </div>
        )}

        {/* Admin Actions */}
        <div className="flex flex-row gap-2">
          <SecondaryButton
            variant="small"
            label={showCreateOrg ? "Annuler" : "Creer une organisation"}
            onClick={() => { setShowCreateOrg(!showCreateOrg); setShowCreateUser(false); }}
          />
          <SecondaryButton
            variant="small"
            label={showCreateUser ? "Annuler" : "Creer un utilisateur"}
            onClick={() => { setShowCreateUser(!showCreateUser); setShowCreateOrg(false); }}
          />
        </div>

        {/* Create Org Form */}
        {showCreateOrg && (
          <div className="flex flex-col gap-3 bg-white p-4 rounded-lg border border-blue-200">
            <div className="text-h2 font-semibold text-black">Nouvelle organisation</div>

            <div className="grid grid-cols-2 gap-3">
              <TextField label="Nom *" value={orgName} onChange={setOrgName} placeholder="Mon Garage SARL" />
              <TextField label="Email *" value={orgEmail} onChange={setOrgEmail} placeholder="contact@mongarage.fr" />
              <TextField label="Telephone" value={orgPhone} onChange={setOrgPhone} placeholder="06 12 34 56 78" />
              <TextField label="SIRET" value={orgSiret} onChange={setOrgSiret} placeholder="123 456 789 00012" />
              <TextField label="Adresse" value={orgAddress} onChange={setOrgAddress} placeholder="12 rue de la Paix" />
              <TextField label="Ville" value={orgCity} onChange={setOrgCity} placeholder="Nantes" />
              <TextField label="Code postal" value={orgPostal} onChange={setOrgPostal} placeholder="44000" />
              <div className="flex flex-col gap-1">
                <div className="text-h4 text-neutral-400">Type</div>
                <DropDown
                  value={orgType}
                  onSelect={(v) => setOrgType(v as string)}
                  options={[
                    { label: "Garage", value: "garage" },
                    { label: "Bureau de controle", value: "bureau_controle" },
                    { label: "Autre", value: "autre" },
                  ]}
                />
              </div>
            </div>

            <div className="flex flex-row justify-end gap-2 mt-2">
              <SecondaryButton variant="small" label="Annuler" onClick={() => setShowCreateOrg(false)} />
              <Button
                label={creatingOrg ? "Creation..." : "Creer l'organisation"}
                onClick={handleCreateOrg}
                disabled={creatingOrg || !orgName || !orgEmail}
              />
            </div>
          </div>
        )}

        {/* Create User Form */}
        {showCreateUser && (
          <div className="flex flex-col gap-3 bg-white p-4 rounded-lg border border-green-200">
            <div className="text-h2 font-semibold text-black">Nouvel utilisateur</div>
            <div className="text-h4 text-neutral-400">
              Cree un utilisateur dans Firestore et l'ajoute a l'organisation specifiee.
            </div>

            <div className="grid grid-cols-2 gap-3">
              <TextField label="Email *" value={newUserEmail} onChange={setNewUserEmail} placeholder="jean@exemple.fr" />
              <TextField label="Nom *" value={newUserName} onChange={setNewUserName} placeholder="Jean Dupont" />
              <TextField label="Org ID *" value={newUserOrgId} onChange={setNewUserOrgId} placeholder="ID de l'organisation" />
              <div className="flex flex-col gap-1">
                <div className="text-h4 text-neutral-400">Niveau d'acces</div>
                <DropDown
                  value={newUserAccessType}
                  onSelect={(v) => setNewUserAccessType(v as string)}
                  options={[
                    { label: "Owner", value: "OWNER" },
                    { label: "Admin", value: "ADMIN" },
                    { label: "Member", value: "MEMBER" },
                  ]}
                />
              </div>
            </div>

            <div className="flex flex-row justify-end gap-2 mt-2">
              <SecondaryButton variant="small" label="Annuler" onClick={() => setShowCreateUser(false)} />
              <Button
                label={creatingUser ? "Creation..." : "Creer l'utilisateur"}
                onClick={handleCreateUser}
                disabled={creatingUser || !newUserEmail || !newUserName || !newUserOrgId}
              />
            </div>
          </div>
        )}

        {/* User List */}
        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border">
          <div className="flex flex-row justify-between items-center mb-2">
            <div className="text-h2 font-semibold text-black">
              Utilisateurs ({filteredUsers.length})
            </div>
          </div>

          <SearchField
            value={search}
            onChange={(value) => setSearch(value)}
            placeholder="Rechercher par nom, email ou org_id..."
          />

          {loading ? (
            <div className="text-h4 text-neutral-400 text-center py-8">
              Chargement des utilisateurs...
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-h4 text-neutral-400 text-center py-8">
              Aucun utilisateur trouve
            </div>
          ) : (
            <div className="flex flex-col divide-y divide-neutral-200 max-h-[70vh] overflow-y-auto">
              {filteredUsers.map((user) => (
                <div
                  key={user.id}
                  className="flex flex-row justify-between items-center py-3 px-2"
                >
                  <div className="flex flex-row items-center gap-3 flex-1 min-w-0">
                    <UserAvatar
                      src={user.customization?.photo_url}
                      className="w-10 h-10 rounded-full border flex-shrink-0"
                      value={user.customization?.avatar_seed || user.infos?.display_name}
                      size={40}
                    />
                    <div className="flex flex-col min-w-0 flex-1">
                      <div className="text-h3 font-medium truncate">
                        {user.infos?.full_name || user.infos?.display_name}
                        {user.id === userData?.id && (
                          <span className="text-h4 text-neutral-400 font-normal ml-2">
                            (Vous)
                          </span>
                        )}
                      </div>
                      <div className="text-h4 text-neutral-400 truncate">
                        {user.infos?.email}
                      </div>
                    </div>
                    <div className="flex flex-col items-end flex-shrink-0 mr-4">
                      <div className="text-h5 text-neutral-300 font-mono">
                        {user.org_id}
                      </div>
                    </div>
                  </div>
                  <SecondaryButton
                    variant="small"
                    label={loginAsLoading === user.id ? "..." : "Login as"}
                    disabled={user.id === userData?.id || loginAsLoading !== null}
                    onClick={() => handleLoginAs(user.id)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
